import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileText, CheckCircle2, XCircle, Clock, Ban, Pencil, X, Eye, Loader2,
  AlertCircle, Download, Send, FilePlus2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface Emissao {
  id: number
  chave_acesso: string | null
  numero: number | null
  serie: number | null
  modelo: string
  ambiente: string
  emissor: string
  status: string
  cStat: string | null
  xMotivo: string | null
  protocolo: string | null
  pedido_id: number | null
  // Origem do documento: venda (pedido) x nota avulsa (transferência, remessa...)
  origem_tipo?: 'venda' | 'avulsa' | 'outra'
  origem_label?: string
  nota_avulsa_id?: number | null
  destinatario?: string
  tentativas: number
  fallback_focus: boolean
  created_at: string | null
  updated_at: string | null
}

interface Evento {
  id: number
  tipo: string
  sequencia: number
  texto: string | null
  status: string
  cStat: string | null
  xMotivo: string | null
  protocolo: string | null
  created_at: string
}

interface DetalheEmissao extends Emissao {
  xml_enviado: string | null
  xml_protocolo: string | null
  eventos: Evento[]
}

const STATUS_LABEL: Record<string, { cor: string; texto: string; Icon: any }> = {
  pendente:    { cor: 'bg-yellow-100 text-yellow-800', texto: 'Pendente', Icon: Clock },
  autorizada:  { cor: 'bg-emerald-100 text-emerald-800', texto: 'Autorizada', Icon: CheckCircle2 },
  rejeitada:   { cor: 'bg-red-100 text-red-800', texto: 'Rejeitada', Icon: XCircle },
  cancelada:   { cor: 'bg-slate-200 text-slate-700', texto: 'Cancelada', Icon: Ban },
}

export default function NFePropriaPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [filtroStatus, setFiltroStatus] = useState<string>('')
  const [fDe, setFDe] = useState('')
  const [fAte, setFAte] = useState('')
  const [fCfop, setFCfop] = useState('')
  const [fCliente, setFCliente] = useState('')
  const [fProduto, setFProduto] = useState<{ id: number; label: string } | null>(null)
  const [buscaProd, setBuscaProd] = useState('')
  const [detalheOpen, setDetalheOpen] = useState<DetalheEmissao | null>(null)
  const [cancelarAlvo, setCancelarAlvo] = useState<Emissao | null>(null)
  const [ccEAlvo, setCcEAlvo] = useState<Emissao | null>(null)

  const { data: produtosBusca = [] } = useQuery<any[]>({
    queryKey: ['nfe-filtro-produtos', buscaProd],
    queryFn: async () => (await api.get('/cadastros/produtos', {
      params: { q: buscaProd.trim(), limit: 10 },
    })).data,
    enabled: buscaProd.trim().length >= 2,
  })

  const { data, isLoading } = useQuery<{ total: number; emissoes: Emissao[] }>({
    queryKey: ['nfe-emissoes', filtroStatus, fDe, fAte, fCfop, fCliente, fProduto?.id],
    queryFn: async () => {
      const params: any = {}
      if (filtroStatus) params.status = filtroStatus
      if (fDe) params.de = fDe
      if (fAte) params.ate = fAte
      if (fCfop.trim()) params.cfop = fCfop.trim()
      if (fCliente.trim()) params.cliente = fCliente.trim()
      if (fProduto) params.produto_id = fProduto.id
      return (await api.get('/nfe-proprio/emissoes', { params })).data
    },
  })

  const reenviar = useMutation({
    mutationFn: async (pedidoId: number) => (await api.post(`/nfe-proprio/emitir/${pedidoId}`)).data,
    onSuccess: (d: any) => {
      if (d?.autorizada) toast.success(`NF-e autorizada${d.numero ? ` — nº ${d.numero}/${d.serie}` : ''}`)
      else toast.error(`SEFAZ ${d?.cStat || ''}: ${d?.xMotivo || d?.erro || 'não autorizada'}`)
      qc.invalidateQueries({ queryKey: ['nfe-emissoes'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao reenviar'),
  })
  const carregarDetalhe = useMutation({
    mutationFn: async (id: number) => (await api.get(`/nfe-proprio/emissoes/${id}`)).data,
    onSuccess: (d) => setDetalheOpen(d),
    onError: () => toast.error('Erro ao carregar detalhes'),
  })

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="text-brand-600" /> NFe Próprio — Histórico
          </h1>
          <p className="text-ink-500 text-sm mt-1">
            NFes emitidas pelo emissor próprio (tabela nfe_emissao). Tambem lista Cancelamentos e CC-e.
          </p>
        </div>
        {/* Porta de entrada da NOTA AVULSA: emitir NF-e sem venda por trás
            (transferência entre lojas, remessa p/ conserto, devolução de compra,
            ativo imobilizado). A emissão a partir de uma venda continua saindo
            pela tela do pedido — esta aqui é só pro que não tem pedido. */}
        <button
          onClick={() => navigate('/fiscal/nota-avulsa')}
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600
                     hover:bg-brand-700 text-white text-sm font-semibold shadow-sm">
          <FilePlus2 size={16} /> Emitir nota avulsa
        </button>
      </div>

      {/* Filtro */}
      <div className="flex gap-2 items-center">
        <span className="text-sm text-ink-600">Filtrar por status:</span>
        {['', 'pendente', 'autorizada', 'rejeitada', 'cancelada'].map((s) => (
          <button
            key={s || 'todos'}
            onClick={() => setFiltroStatus(s)}
            className={`px-3 py-1 rounded text-sm ${
              filtroStatus === s
                ? 'bg-brand-600 text-white'
                : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
            }`}
          >
            {s || 'todos'}
          </button>
        ))}
      </div>

      {/* Filtros: período, CFOP, cliente/fornecedor e produto (pedido do cliente 23/09).
          Valem pra nota de venda e pra nota avulsa. */}
      <div className="flex flex-wrap gap-2 items-end bg-white border border-ink-200 rounded-lg p-3">
        <div>
          <label className="label">De</label>
          <input type="date" className="input" value={fDe} onChange={e => setFDe(e.target.value)} />
        </div>
        <div>
          <label className="label">Até</label>
          <input type="date" className="input" value={fAte} onChange={e => setFAte(e.target.value)} />
        </div>
        <div>
          <label className="label">CFOP</label>
          <input className="input w-24 font-mono" placeholder="5102" value={fCfop}
            onChange={e => setFCfop(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="label">Cliente / fornecedor</label>
          <input className="input" placeholder="Nome ou CNPJ/CPF" value={fCliente}
            onChange={e => setFCliente(e.target.value)} />
        </div>
        <div className="relative min-w-[200px]">
          <label className="label">Produto</label>
          {fProduto ? (
            <div className="input flex items-center justify-between gap-2">
              <span className="truncate text-sm">{fProduto.label}</span>
              <button onClick={() => { setFProduto(null); setBuscaProd('') }}
                className="text-ink-400 hover:text-rose-600"><X size={13} /></button>
            </div>
          ) : (
            <>
              <input className="input" placeholder="Buscar produto…" value={buscaProd}
                onChange={e => setBuscaProd(e.target.value)} />
              {produtosBusca.length > 0 && buscaProd.trim().length >= 2 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-ink-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                  {produtosBusca.map((p: any) => (
                    <button key={p.id} type="button"
                      onClick={() => { setFProduto({ id: p.id, label: `${p.codigo} · ${p.descricao}` }); setBuscaProd('') }}
                      className="w-full px-3 py-2 text-left hover:bg-brand-50 border-b last:border-0 text-xs">
                      <span className="font-mono text-ink-400 mr-2">{p.codigo}</span>{p.descricao}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {(fDe || fAte || fCfop || fCliente || fProduto) && (
          <button onClick={() => { setFDe(''); setFAte(''); setFCfop(''); setFCliente(''); setFProduto(null); setBuscaProd('') }}
            className="btn-ghost text-xs">Limpar</button>
        )}
      </div>

      {/* Tabela */}
      {/* overflow-x-auto + min-w: com 9 colunas a tabela espremia tudo e comia a
          coluna de Ações. Agora ela rola na horizontal e as Ações ficam fixas
          à direita, sempre visíveis. */}
      <div className="bg-white border border-ink-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-ink-50">
            <tr>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Nº</th>
              <th className="hidden xl:table-cell text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Chave</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Origem</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Destinatário</th>
              <th className="hidden lg:table-cell text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Ambiente</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Status</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Motivo SEFAZ</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Emissão</th>
              <th className="sticky right-0 z-10 bg-ink-50 text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && (
              <tr><td colSpan={9} className="text-center py-6 text-ink-400">Carregando...</td></tr>
            )}
            {!isLoading && (!data?.emissoes || data.emissoes.length === 0) && (
              <tr>
                <td colSpan={9} className="text-center py-10">
                  <AlertCircle className="mx-auto mb-2 text-ink-300" size={32} />
                  <p className="text-ink-500">Nenhuma NFe emitida ainda.</p>
                  <p className="text-ink-400 text-xs mt-1">
                    Emita a primeira via <strong>Vendas → Preview NFe própria</strong>,
                    ou use <strong>Emitir nota avulsa</strong> (acima) pra uma nota sem venda.
                  </p>
                </td>
              </tr>
            )}
            {data?.emissoes.map((e) => {
              const st = STATUS_LABEL[e.status] || { cor: 'bg-ink-100', texto: e.status, Icon: FileText }
              const podeEventos = e.status === 'autorizada'
              return (
                <tr key={e.id}
                  onClick={() => carregarDetalhe.mutate(e.id)}
                  className="group hover:bg-violet-50 cursor-pointer">
                  <td className="px-3 py-2.5 font-mono text-xs">
                    {e.numero ? `${e.numero}/${e.serie}` : '—'}
                  </td>
                  <td className="hidden xl:table-cell px-3 py-2.5 font-mono text-[10px] text-ink-500">
                    {e.chave_acesso ? `...${e.chave_acesso.slice(-12)}` : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {e.origem_tipo === 'avulsa' ? (
                      <button
                        onClick={(ev) => {
                          ev.stopPropagation()
                          navigate(`/fiscal/nota-avulsa/${e.nota_avulsa_id}`)
                        }}
                        title={`Nota avulsa #${e.nota_avulsa_id} — abrir`}
                        className="inline-flex items-center gap-1 max-w-[180px]">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700 shrink-0">
                          AVULSA
                        </span>
                        <span className="truncate text-ink-600">{e.origem_label}</span>
                      </button>
                    ) : e.origem_tipo === 'venda' ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-700">
                          VENDA
                        </span>
                        <span className="text-ink-500">#{e.pedido_id}</span>
                      </span>
                    ) : (
                      <span className="text-ink-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs max-w-[180px] truncate" title={e.destinatario || ''}>
                    {e.destinatario || '—'}
                  </td>
                  <td className="hidden lg:table-cell px-3 py-2.5 text-xs">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      (e.ambiente === '1' || e.ambiente === 'producao') ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {(e.ambiente === '1' || e.ambiente === 'producao') ? 'PRODUÇÃO' : 'homologação'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${st.cor}`}>
                      <st.Icon size={12} /> {st.texto}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-ink-600 max-w-[220px] truncate" title={e.xMotivo || ''}>
                    {e.cStat && <span className="font-mono text-[10px] mr-1">({e.cStat})</span>}
                    {e.xMotivo || '—'}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-ink-500">
                    {e.created_at ? new Date(e.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td className="sticky right-0 z-10 bg-white group-hover:bg-violet-50 px-3 py-2.5 text-right shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]"
                    onClick={ev => ev.stopPropagation()}>
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => carregarDetalhe.mutate(e.id)}
                        className="text-ink-500 hover:text-brand-600 p-1"
                        title="Detalhes"
                      >
                        <Eye size={14} />
                      </button>
                      {(e.status === 'rejeitada' || e.status === 'pendente') && e.pedido_id && (
                        <button
                          onClick={() => { if (confirm('Reenviar esta NF-e para a SEFAZ?')) reenviar.mutate(e.pedido_id!) }}
                          disabled={reenviar.isPending}
                          className="text-emerald-600 hover:text-emerald-800 p-1 disabled:opacity-40"
                          title="Reenviar para a SEFAZ"
                        >
                          {reenviar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        </button>
                      )}
                      {podeEventos && (
                        <>
                          <button
                            onClick={() => setCcEAlvo(e)}
                            className="text-amber-600 hover:text-amber-800 p-1"
                            title="Emitir CC-e"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => setCancelarAlvo(e)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Cancelar NFe"
                          >
                            <Ban size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {detalheOpen && <DetalheModal det={detalheOpen} onClose={() => setDetalheOpen(null)} />}
      {cancelarAlvo && (
        <CancelarModal
          emissao={cancelarAlvo}
          onClose={() => setCancelarAlvo(null)}
          onSuccess={() => { qc.invalidateQueries({ queryKey: ['nfe-emissoes'] }); setCancelarAlvo(null) }}
        />
      )}
      {ccEAlvo && (
        <CCeModal
          emissao={ccEAlvo}
          onClose={() => setCcEAlvo(null)}
          onSuccess={() => { qc.invalidateQueries({ queryKey: ['nfe-emissoes'] }); setCcEAlvo(null) }}
        />
      )}
    </div>
  )
}


// ============================================================
// Modais
// ============================================================
function DetalheModal({ det, onClose }: { det: DetalheEmissao; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Detalhe NFe #{det.id}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Número/Série" value={det.numero ? `${det.numero}/${det.serie}` : '—'} />
            <Info label="Status" value={det.status} />
            <Info label="Ambiente" value={(det.ambiente === '1' || det.ambiente === 'producao') ? 'Produção' : 'Homologação'} />
            <Info label="Emissor" value={det.emissor} />
            <Info label="cStat / Motivo" value={`${det.cStat || '—'} / ${det.xMotivo || '—'}`} />
            <Info label="Protocolo" value={det.protocolo || '—'} />
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase font-semibold mb-1">Chave de Acesso</div>
            <div className="font-mono text-xs break-all bg-ink-50 p-2 rounded">{det.chave_acesso || '—'}</div>
          </div>
          {det.xml_enviado && (
            <details>
              <summary className="cursor-pointer text-xs font-semibold text-ink-600">XML enviado</summary>
              <pre className="text-[10px] bg-ink-50 p-2 rounded mt-1 max-h-60 overflow-auto">{det.xml_enviado}</pre>
            </details>
          )}
          <div>
            <div className="text-xs text-ink-500 uppercase font-semibold mb-2">
              Eventos ({det.eventos.length})
            </div>
            {det.eventos.length === 0 ? (
              <div className="text-xs text-ink-400 italic">Sem eventos</div>
            ) : (
              <div className="space-y-2">
                {det.eventos.map((ev) => (
                  <div key={ev.id} className="border border-ink-200 rounded p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">
                        {ev.tipo.replace('_', ' ')} {ev.sequencia > 1 && `#${ev.sequencia}`}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                        ev.status === 'homologado' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {ev.status}
                      </span>
                    </div>
                    <div className="text-ink-600 mt-1">{ev.texto}</div>
                    <div className="text-ink-400 text-[10px] mt-1">
                      {ev.cStat && `(${ev.cStat}) `}{ev.xMotivo} — {new Date(ev.created_at).toLocaleString('pt-BR')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}


function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-ink-500 uppercase font-semibold">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  )
}


function CancelarModal({
  emissao, onClose, onSuccess,
}: { emissao: Emissao; onClose: () => void; onSuccess: () => void }) {
  const [justif, setJustif] = useState('')
  // FIX (18/09): esta tela cancelava NF-e só com justificativa, enquanto a tela do
  // pedido e a de nota avulsa exigem senha de cargo. Cancelar nota fiscal é ação
  // séria demais pra ter dois níveis de exigência dependendo de onde se clica.
  const [senha, setSenha] = useState('')
  const mut = useMutation({
    mutationFn: async () => {
      // Valida senha + cargo (admin/gerente/supervisor). NÃO cancela nada aqui:
      // só passa do 403 é que dispara o cancelamento de verdade.
      await api.post('/auth/autorizar-exclusao', { senha })
      return (await api.post(`/nfe-proprio/cancelar/${emissao.id}`, { justificativa: justif })).data
    },
    onSuccess: (d: { sucesso: boolean; xMotivo?: string; erro?: string }) => {
      if (d.sucesso) {
        toast.success(`NFe cancelada! ${d.xMotivo || ''}`)
        onSuccess()
      } else {
        toast.error(d.erro || d.xMotivo || 'Falha no cancelamento')
      }
    },
    onError: (e: any) => {
      // 403 aqui quase sempre é a senha, não o cancelamento em si.
      const msg = e?.response?.data?.detail
      toast.error(e?.response?.status === 403
        ? (msg || 'Senha de autorização incorreta.')
        : (msg || 'Erro ao cancelar'))
    },
  })

  const valido = justif.trim().length >= 15 && justif.trim().length <= 255 && !!senha
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Ban className="text-red-600" size={18} /> Cancelar NFe #{emissao.numero}
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2 rounded">
            ⚠ Prazo legal: até 24h após a autorização. Cancelamentos fora desse prazo exigem processo extemporâneo.
          </div>
          <div>
            <label className="label">Justificativa (15-255 caracteres)</label>
            <textarea
              className="input"
              rows={3}
              value={justif}
              onChange={(e) => setJustif(e.target.value.slice(0, 255))}
              placeholder="Motivo do cancelamento"
            />
            <div className="text-xs text-ink-500 mt-1">{justif.trim().length}/255</div>
          </div>
          <div>
            <label className="label">Senha de autorização</label>
            <input
              type="password"
              className="input"
              value={senha}
              autoComplete="new-password"
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Senha de admin, gerente ou supervisor"
            />
            <p className="text-[11px] text-ink-500 mt-1">
              Se o seu usuário já tem cargo, use a sua própria senha.
            </p>
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost flex-1" onClick={onClose}>Voltar</button>
            <button
              className="btn-primary flex-1 bg-red-600 hover:bg-red-700 border-red-600"
              disabled={!valido || mut.isPending}
              onClick={() => mut.mutate()}
            >
              {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Cancelar NFe'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}


function CCeModal({
  emissao, onClose, onSuccess,
}: { emissao: Emissao; onClose: () => void; onSuccess: () => void }) {
  const [texto, setTexto] = useState('')
  const mut = useMutation({
    mutationFn: async () =>
      (await api.post(`/nfe-proprio/cc-e/${emissao.id}`, { texto })).data,
    onSuccess: (d: { sucesso: boolean; xMotivo?: string; erro?: string }) => {
      if (d.sucesso) {
        toast.success(`CC-e emitida! ${d.xMotivo || ''}`)
        onSuccess()
      } else {
        toast.error(d.erro || d.xMotivo || 'Falha na emissao da CC-e')
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao emitir CC-e'),
  })

  const valido = texto.trim().length >= 15 && texto.trim().length <= 1000
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Pencil className="text-amber-600" size={18} /> Carta de Correção (CC-e) — NFe #{emissao.numero}
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2 rounded space-y-1">
            <p>⚠ <strong>CC-e NÃO pode alterar</strong>: valores, destinatário, datas, quantidades ou produtos.</p>
            <p>Use apenas para erros de texto (razão social com typo, endereço, natureza da operação, etc).</p>
            <p>Limite: 20 CC-e por NFe (raramente precisa mais de 2).</p>
          </div>
          <div>
            <label className="label">Texto da correção (15-1000 caracteres)</label>
            <textarea
              className="input"
              rows={5}
              value={texto}
              onChange={(e) => setTexto(e.target.value.slice(0, 1000))}
              placeholder="Ex: Onde se le 'Rua X', leia-se 'Rua Y'"
            />
            <div className="text-xs text-ink-500 mt-1">{texto.trim().length}/1000</div>
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost flex-1" onClick={onClose}>Voltar</button>
            <button
              className="btn-primary flex-1 bg-amber-600 hover:bg-amber-700 border-amber-600"
              disabled={!valido || mut.isPending}
              onClick={() => mut.mutate()}
            >
              {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Emitir CC-e'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
